<template>
  <a-modal
    :visible="visible"
    :destroy-on-close="true"
    title="Create User"
    ok-text="Create"
    :ok-button-props="{ props: { disabled: false, loading: creatingUser } }"
    cancel-text="Cancel"
    @ok="() => handleCreateUser()"
    @cancel="() => handleClose()"
  >
    <a-form layout="horizontal">
      <a-form-item
        :label-col="{ span: 7 }"
        :wrapper-col="{ span: 17 }"
        :validate-status="$validator.errors.first('create-user.full_name') ? 'error' : ''"
        :help="$validator.errors.first('create-user.full_name')"
        label="Full name"
      >
        <a-input
          v-model="newUserForm.full_name"
          v-validate="'required|max:255'"
          data-vv-scope="create-user"
          data-vv-name="full_name"
          data-vv-as="full name"
          placeholder="Name Surname"
        />
      </a-form-item>

      <a-form-item
        :label-col="{ span: 7 }"
        :wrapper-col="{ span: 17 }"
        :validate-status="$validator.errors.first('create-user.email') ? 'error' : ''"
        :help="$validator.errors.first('create-user.email')"
        label="Email"
      >
        <a-input
          v-model="newUserForm.email"
          v-validate="'required|email|max:255'"
          data-vv-scope="create-user"
          data-vv-name="email"
          data-vv-as="email"
          placeholder="jane.doe@example.com"
        />
      </a-form-item>

      <a-form-item
        :label-col="{ span: 7 }"
        :wrapper-col="{ span: 17 }"
        :validate-status="$validator.errors.first('create-user.start_date') ? 'error' : ''"
        :help="$validator.errors.first('create-user.start_date')"
        label="Starting date"
      >
        <a-date-picker
          v-validate="'required'"
          :value="parseDate(newUserForm.start_date)"
          data-vv-scope="create-user"
          data-vv-name="start_date"
          data-vv-as="starting date"
          class="w-100"
          @change="handleCreateUserStartDateChange"
        />
      </a-form-item>
    </a-form>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, getCurrentInstance, watch } from "vue";
import useFilters from "@/useFilters";
import { apiCreateUser } from "../_utils/api";

const props = defineProps({
  visible: { type: Boolean, default: false },
});

const emits = defineEmits(["close", "user-created"]);

const { parseDate } = useFilters();
const instance = getCurrentInstance();
const $validator = instance?.proxy?.$validator;
const $message = instance?.proxy?.$message;

const newUserForm = ref<any>({
  full_name: undefined,
  email: undefined,
  start_date: undefined,
});

const creatingUser = ref<boolean>(false);

watch(
  () => props.visible,
  (newProp) => {
    if (newProp) {
      $validator?.reset("create-user");
    }
  }
);

const handleClose = (): void => {
  $validator?.reset("create-user");
  newUserForm.value = {
    full_name: undefined,
    email: undefined,
    start_date: undefined,
  };
  emits("close");
};

const handleCreateUserStartDateChange = (date: any): void => {
  newUserForm.value.start_date = date ? date.format("YYYY-MM-DD") : undefined;
};

const handleCreateUser = async (): Promise<void> => {
  const isValid = await $validator?.validateAll("create-user");
  if (!isValid) return;

  creatingUser.value = true;

  try {
    const payload = {
      name: newUserForm.value.full_name,
      email: newUserForm.value.email,
      start_date: newUserForm.value.start_date,
    };

    await apiCreateUser(payload);
    $message?.success("User created successfully!", 3);
    emits("user-created");
    handleClose();
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.errors?.email?.[0] ||
      error.response?.data?.email?.[0] ||
      error.message ||
      "Failed to create user";
    $message?.error(errorMessage, 3);
  } finally {
    creatingUser.value = false;
  }
};
</script>
